/**
 * A sideways swipe on an element, for any widget that pages or turns a carousel. It is the finger's gesture, so it
 * is measured in screen px, not in the scaled canvas's: the same flick on a phone and on the totem (1:1).
 *
 * A swipe never also lands as a tap on what the finger started on (a card, a price): after a drag the click is
 * swallowed. Off (`enabled` false: the mode says no swipe, a replay), it adds nothing to the element.
 *
 * With `follow`, the element `track` is put on follows the finger (moved by its transform, not by React: smooth on a slow phone), resists
 * where there is nothing (`can`), and at the release slides one width over or springs back. Slid over, it waits for
 * what is shown (`at`) to change — the backend's page, the train in focus — and is at rest on it at once; if nothing
 * changes it springs back. With less motion asked (`data-motion="reduce"`) it does not move: the swipe acts at the release.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent, type RefObject } from 'react';

export type SwipeDirection = 'next' | 'prev';

/** The shortest swipe, in screen px. */
export const SWIPE_MIN_PX = 48;
/** How many times more sideways than up or down a move must be to be a swipe, not a scroll or a slip. */
export const SWIPE_DOMINANCE = 1.5;
/** Up to this far, in screen px, a touch is still a tap. */
export const TAP_SLOP_PX = 10;
/** Where there is nothing to turn to, the track follows the finger this much: it resists, it does not stop dead. */
export const EDGE_RESISTANCE = 0.3;
export const SETTLE_MS = 250;
/** Slid over, how long the track waits for the content to follow before it springs back (a refused page). */
export const CONFIRM_MS = 2000;
const SETTLE = `transform ${SETTLE_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`;

/** The finger moved left: what comes after is wanted ('next'); right: what came before. */
export function swipeOf(dx: number, dy: number): SwipeDirection | null {
  if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < SWIPE_DOMINANCE * Math.abs(dy)) return null;
  return dx < 0 ? 'next' : 'prev';
}

export interface SwipeOptions {
  enabled: boolean;
  onSwipe(direction: SwipeDirection): void;
  /** Whether there is something that way; where not, the drag resists and the release turns nothing. */
  can?(direction: SwipeDirection): boolean;
  /** A track (`Swipe.track`: a row of pages, a card) follows the finger; without, the swipe acts at the release only. */
  follow?: boolean;
  /** What is shown now: when it changes, the track is at rest on it. */
  at?: unknown;
}

export interface SwipeProps {
  style: CSSProperties;
  onPointerDown(e: PointerEvent<Element>): void;
  onPointerMove(e: PointerEvent<Element>): void;
  onPointerUp(e: PointerEvent<Element>): void;
  onPointerCancel(): void;
  onClickCapture(e: MouseEvent<Element>): void;
}

export interface Swipe<T extends HTMLElement> {
  /** Props to spread on the element the finger swipes. */
  area: Partial<SwipeProps>;
  /** The ref of what follows the finger, with `follow`. */
  track: RefObject<T | null>;
  /** The track is off its rest (dragged, settling, slid over): draw what is beside it. */
  moving: boolean;
}

interface Start {
  id: number;
  x: number;
  y: number;
  /** Screen px per canvas px: the canvas is scaled to fit. */
  scale: number;
  follows: boolean;
}

// The browser keeps the vertical scroll (an itinerary, a long card); the sideways drag is the page's.
const PAN_Y: CSSProperties = { touchAction: 'pan-y' };
const always = () => true;

export function useSwipe<T extends HTMLElement = HTMLElement>({ enabled, onSwipe, can = always, follow = false, at }: SwipeOptions): Swipe<T> {
  const track = useRef<T>(null);
  const start = useRef<Start | null>(null);
  const dragged = useRef(false);
  const waiting = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // moving belongs to what was shown when it started: once that changes, nothing is beside it any more
  const [movingAt, setMovingAt] = useState<{ at: unknown } | null>(null);

  useLayoutEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    waiting.current = false;
    const el = track.current;
    if (el) {
      el.style.transition = '';
      el.style.transform = '';
    }
  }, [at]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  if (!enabled) return { area: {}, moving: false, track };

  const place = (x: number, animate: boolean) => {
    const el = track.current;
    if (!el) return;
    el.style.transition = animate ? SETTLE : '';
    el.style.transform = x ? `translateX(${x}px)` : '';
  };
  const after = (ms: number, then: () => void) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      then();
    }, ms);
  };
  const springBack = () => {
    waiting.current = false;
    place(0, true);
    after(SETTLE_MS, () => setMovingAt(null));
  };
  const far = (e: PointerEvent<Element>, from: Start) => Math.hypot(e.clientX - from.x, e.clientY - from.y) > TAP_SLOP_PX;

  return {
    track,
    moving: movingAt !== null && movingAt.at === at,
    area: {
      style: PAN_Y,
      onPointerDown(e) {
        dragged.current = false;
        // a second finger: a pinch, not a swipe; a mouse's other buttons are not a drag; slid over: wait for it
        if (!e.isPrimary || e.button > 0 || waiting.current) {
          start.current = null;
          return;
        }
        const el = e.currentTarget as HTMLElement;
        const width = el.getBoundingClientRect().width;
        start.current = {
          id: e.pointerId,
          x: e.clientX,
          y: e.clientY,
          scale: width && el.offsetWidth ? width / el.offsetWidth : 1,
          follows: follow && !!track.current && !el.closest('[data-motion="reduce"]'),
        };
      },
      onPointerMove(e) {
        const from = start.current;
        if (!from || from.id !== e.pointerId) return;
        if (!dragged.current) {
          if (!far(e, from)) return;
          dragged.current = true;
          // captured only once it is a drag, so a tap still clicks what is under the finger; the release then comes
          // here even off the element. It throws for a pointer already gone, and must never stop the swipe.
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch { /* the pointer is gone: nothing to capture */ }
          if (from.follows) {
            if (timer.current) clearTimeout(timer.current);
            setMovingAt({ at });
          }
        }
        if (!from.follows) return;
        let dx = (e.clientX - from.x) / from.scale;
        if (dx && !can(dx < 0 ? 'next' : 'prev')) dx *= EDGE_RESISTANCE;
        place(dx, false);
      },
      onPointerUp(e) {
        const from = start.current;
        if (!from || from.id !== e.pointerId) return;
        start.current = null;
        if (far(e, from)) dragged.current = true;
        const direction = swipeOf(e.clientX - from.x, e.clientY - from.y);
        const go = direction && can(direction) ? direction : null;
        if (from.follows && dragged.current) {
          if (go) {
            const width = track.current?.offsetWidth ?? 0;
            waiting.current = true;
            place(go === 'next' ? -width : width, true);
            after(CONFIRM_MS, springBack);
          } else {
            springBack();
          }
        }
        if (go) onSwipe(go);
      },
      onPointerCancel() {
        const from = start.current;
        start.current = null;
        if (from?.follows && dragged.current) springBack(); // the browser took the gesture over (a scroll)
      },
      onClickCapture(e) {
        if (!dragged.current) return;
        dragged.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    },
  };
}
